import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { adminAPI, partialAPI, mockAPI, batchAPI, authAPI } from '@/api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Calendar, Users, BookOpen, TrendingUp, Download, UserPlus, LogOut, Coins, Layers, Lock, User as UserIcon, Shield, FileDown, AlertCircle, Check, ChevronsUpDown, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import BatchManagement from '@/components/admin/BatchManagement';
import StudentProfile from '@/components/admin/StudentProfile';
import RecentBookingsSection from '@/components/admin/RecentBookingsSection';
import ModeratorsManagement from '@/components/admin/ModeratorsManagement';
import AdminActivityLog from '@/components/admin/AdminActivityLog';
import SpeakingDatesView from '@/components/admin/SpeakingDatesView';
import PartialExamsDateView from '@/components/admin/PartialExamsDateView';
import CreateExamModal from '@/components/admin/CreateExamModal';
import EmailQueueManager from '@/components/admin/EmailQueueManager';
import SystemTools from '@/components/admin/SystemTools';
import MessagesManagement from '@/components/admin/MessagesManagement';
import ExternalApproval from '@/components/admin/ExternalApproval';
import ExportNumbers from '@/components/admin/ExportNumbers';
import EmailNotificationSender from '@/components/admin/EmailNotificationSender';
import NoticeManagement from '@/components/admin/NoticeManagement';
import { formatDate, formatTime12Hour, formatTimeRange } from '@/utils/formatters';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Check if current user is admin (not moderator)
  const isAdmin = user?.role === 'admin';
  const isModerator = user?.role === 'moderator';
  
  // Done Exams Modal State
  const [showDoneExamsModal, setShowDoneExamsModal] = useState(false);
  
  // Manual Booking State
  const [showManualBooking, setShowManualBooking] = useState(false);
  const [manualBookingType, setManualBookingType] = useState('partial');
  const [studentType, setStudentType] = useState('batch'); // 'batch' or 'other'
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentSearchOpen, setStudentSearchOpen] = useState(false); // For searchable dropdown
  const [studentSearchQuery, setStudentSearchQuery] = useState(''); // Manual search query for student filter
  const [batchStudentPreviousMock, setBatchStudentPreviousMock] = useState(null);  // Previous mock for batch students
  const [batchStudentMockNumber, setBatchStudentMockNumber] = useState('');  // Which mock number for batch students
  const [otherStudentData, setOtherStudentData] = useState({
    name: '',
    batch_no: '',
    candidate_no: '',
    phone: '',
    email: '',  // Optional email field
    previous_mock_at_radiance: null,  // Yes/No for CD Mock - null means not answered
    radiance_mock_number: ''  // Which mock number at Radiance (1, 2, 3...)
  });
  const [selectedSlot, setSelectedSlot] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingManualSlots, setLoadingManualSlots] = useState(false);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [manualBookingFilters, setManualBookingFilters] = useState({
    module: 'LISTENING',
    exam_date: '',
    category: ''  // CD or PB
  });
  
  // Mock Speaking Slot Selection
  const [showSpeakingSlotModal, setShowSpeakingSlotModal] = useState(false);
  const [selectedMockForSpeaking, setSelectedMockForSpeaking] = useState(null);
  const [availableSpeakingSlots, setAvailableSpeakingSlots] = useState([]);
  const [selectedSpeakingSlot, setSelectedSpeakingSlot] = useState('');
  const [loadingSpeakingSlots, setLoadingSpeakingSlots] = useState(false);
  const [manualSpeakingMode, setManualSpeakingMode] = useState('OFFLINE');
  const [manualWhatsappNumber, setManualWhatsappNumber] = useState('');
  
  // Student Profile Modal
  const [showStudentProfile, setShowStudentProfile] = useState(false);
  const [selectedStudentForProfile, setSelectedStudentForProfile] = useState(null);
  
  // Batch filter
  const [batches, setBatches] = useState([]);
  const [selectedBatchFilter, setSelectedBatchFilter] = useState('all');
  
  // Exams List
  const [partialExams, setPartialExams] = useState([]);
  const [mockExams, setMockExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(false);
  const [selectedPartialExams, setSelectedPartialExams] = useState([]);
  
  // Exam Tab State (Active vs Past)
  const [examTabType, setExamTabType] = useState('active'); // 'active' or 'past'
  const [selectedMockExams, setSelectedMockExams] = useState([]);
  
  // Partial Exams Filter
  const [partialExamModuleFilter, setPartialExamModuleFilter] = useState('ALL');
  
  // Speaking Slots Management
  const [showSpeakingSlotsModal, setShowSpeakingSlotsModal] = useState(false);
  
  // Fix Occupancy State
  const [fixingOccupancy, setFixingOccupancy] = useState(false);
  const [occupancyFixResult, setOccupancyFixResult] = useState(null);
  const [selectedMockForSlots, setSelectedMockForSlots] = useState(null);
  const [speakingSlots, setSpeakingSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Create Exam State (Modal handled by CreateExamModal component)
  const [showCreateExam, setShowCreateExam] = useState(false);
  
  // Export modal states
  const [showPartialExportModal, setShowPartialExportModal] = useState(false);
  const [showMockExportModal, setShowMockExportModal] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    module: '',
    exam_id: '',
    category: '',
    mock_id: ''
  });
  const [availableExams, setAvailableExams] = useState([]);
  const [availableMocks, setAvailableMocks] = useState([]);
  const [exportingCSV, setExportingCSV] = useState(false);

  // Filter students based on manual search query (for manual booking dropdown)
  const filteredStudents = useMemo(() => {
    if (!studentSearchQuery.trim()) return students;
    const query = studentSearchQuery.toLowerCase().trim();
    const words = query.split(/\s+/);
    return students.filter(student => {
      const searchStr = `${student.name || ''} ${student.batch_code || ''} ${student.email || ''} ${student.phone || ''}`.toLowerCase();
      return words.every(word => searchStr.includes(word));
    });
  }, [students, studentSearchQuery]);

  useEffect(() => {
    // Set page title
    document.title = 'Admin Dashboard | Radiance Education';
    
    fetchStats();
    fetchStudents();
    fetchBatches();
    fetchExams('active'); // Always start with active exams
    
    // Auto-refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Fetch exams when exam tab type changes
  useEffect(() => {
    if (examTabType) {
      fetchExams(examTabType);
    }
  }, [examTabType]);
  
  useEffect(() => {
    // Fetch students when batch filter changes
    fetchStudents();
  }, [selectedBatchFilter]);

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      toast.success('Password changed successfully!');
      
      // Reset form
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setShowPasswordChange(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  const handleFixCancelledSlots = async () => {
    if (!window.confirm('This will fix all cancelled booking slots. Continue?')) {
      return;
    }
    
    setFixingOccupancy(true);
    setOccupancyFixResult(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/fix-cancelled-slot-occupancy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fix occupancy');
      }
      
      const result = await response.json();
      setOccupancyFixResult(result);
      
      if (result.fixed > 0) {
        toast.success(`Fixed ${result.fixed} slot(s)! Cancelled slots are now available.`);
      } else {
        toast.info('No issues found. All slots are correct!');
      }
      
      // Refresh data
      fetchStats();
    } catch (error) {
      console.error('Failed to fix occupancy:', error);
      toast.error('Failed to fix cancelled slots');
    } finally {
      setFixingOccupancy(false);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedBatchFilter !== 'all') {
        params.batch = selectedBatchFilter;
      }
      const response = await adminAPI.getStudents(params);
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getList();
      setBatches(response.data);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    }
  };

  const fetchExams = async (timeFilter = 'active') => {
    setLoadingExams(true);
    try {
      const [partialRes, mockRes] = await Promise.all([
        adminAPI.getAvailablePartialSlots({ time_filter: timeFilter }),
        adminAPI.getAvailableMockSlots({ time_filter: timeFilter })
      ]);
      setPartialExams(partialRes.data);
      setMockExams(mockRes.data);
    } catch (error) {
      console.error('Failed to fetch exams:', error);
      toast.error('Failed to load exams');
    } finally {
      setLoadingExams(false);
    }
  };

  const fetchSpeakingSlots = async (mockId) => {
    setLoadingSlots(true);
    try {
      const response = await adminAPI.getMockSpeakingSlots(mockId);
      setSpeakingSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch speaking slots:', error);
      toast.error('Failed to load speaking slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDeleteSpeakingSlot = async (slotId) => {
    if (!confirm('Are you sure you want to delete this speaking slot?')) return;
    
    try {
      await adminAPI.deleteSpeakingSlot(slotId);
      toast.success('Speaking slot deleted');
      // Refresh slots
      fetchSpeakingSlots(selectedMockForSlots.id);
    } catch (error) {
      console.error('Failed to delete slot:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete slot');
    }
  };

  const handleDeletePartialExam = async (examId) => {
    if (!confirm('Are you sure you want to delete this exam? This cannot be undone.')) return;
    
    try {
      await adminAPI.deletePartialExam(examId);
      toast.success('Partial exam deleted');
      fetchExams();
    } catch (error) {
      console.error('Failed to delete exam:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete exam');
    }
  };

  const handleDeleteMockExam = async (examId) => {
    if (!confirm('Are you sure you want to delete this mock exam? This cannot be undone.')) return;
    
    try {
      await adminAPI.deleteMockExam(examId);
      toast.success('Mock exam deleted');
      fetchExams();
    } catch (error) {
      console.error('Failed to delete exam:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete exam';
      
      // If error is about active bookings, offer force delete
      if (errorMsg.includes('active bookings') || errorMsg.includes('Cannot delete')) {
        const forceDelete = confirm(
          '⚠️ This exam has active bookings.\n\n' +
          'FORCE DELETE will:\n' +
          '✅ Refund credits to self-booked students\n' +
          '✅ Send email notifications\n' +
          '✅ Delete all bookings and cleanup data\n\n' +
          'Do you want to FORCE DELETE this exam?'
        );
        
        if (forceDelete) {
          try {
            const response = await adminAPI.forceDeleteMockExam(examId);
            
            // Show detailed summary
            const summary = response.data.refund_summary;
            toast.success(
              `Mock exam force deleted!\n` +
              `✅ ${summary.self_booked_students} students refunded\n` +
              `✅ ${summary.emails_sent} emails sent\n` +
              `✅ ${summary.institute_booked_students} institute bookings removed`
            );
            
            fetchExams();
          } catch (forceError) {
            console.error('Force delete failed:', forceError);
            toast.error('Force delete failed: ' + (forceError.response?.data?.detail || 'Unknown error'));
          }
        }
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleBulkDeletePartial = async () => {
    if (selectedPartialExams.length === 0) {
      toast.error('Please select exams to delete');
      return;
    }
    
    if (!confirm(`Delete ${selectedPartialExams.length} selected partial exam(s)? This cannot be undone.`)) return;
    
    let deleted = 0;
    let failed = 0;
    
    for (const examId of selectedPartialExams) {
      try {
        await adminAPI.deletePartialExam(examId);
        deleted++;
      } catch (error) {
        failed++;
        console.error(`Failed to delete exam ${examId}:`, error);
      }
    }
    
    toast.success(`Deleted ${deleted} exam(s)${failed > 0 ? `, ${failed} failed` : ''}`);
    setSelectedPartialExams([]);
    fetchExams();
  };

  const handleBulkDeleteMock = async () => {
    if (selectedMockExams.length === 0) {
      toast.error('Please select exams to delete');
      return;
    }
    
    if (!confirm(`Delete ${selectedMockExams.length} selected mock exam(s)? This cannot be undone.`)) return;
    
    let deleted = 0;
    let failed = 0;
    
    for (const examId of selectedMockExams) {
      try {
        await adminAPI.deleteMockExam(examId);
        deleted++;
      } catch (error) {
        failed++;
        console.error(`Failed to delete exam ${examId}:`, error);
      }
    }
    
    toast.success(`Deleted ${deleted} exam(s)${failed > 0 ? `, ${failed} failed` : ''}`);
    setSelectedMockExams([]);
    fetchExams();
  };

  const handleDeleteStudent = async (studentId, studentName) => {
    if (!confirm(`Are you sure you want to delete ${studentName}? All their data will be permanently removed.`)) return;
    
    try {
      await adminAPI.deleteStudent(studentId);
      toast.success('Student deleted');
      fetchStudents();
    } catch (error) {
      console.error('Failed to delete student:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete student');
    }
  };

  const handleToggleStudentActive = async (studentId, studentName, currentStatus) => {
    const action = currentStatus ? 'pause' : 'resume';
    if (!confirm(`Are you sure you want to ${action} login for ${studentName}?`)) return;
    
    try {
      await adminAPI.toggleStudentActive(studentId);
      toast.success(`Student login ${currentStatus ? 'paused' : 'resumed'}`);
      fetchStudents();
    } catch (error) {
      console.error('Failed to toggle student status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update student status');
    }
  };

  const fetchExamsForExport = async (module, category = '') => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/exams?module=${module}`;
      if (category && module !== 'SPEAKING') {
        // Speaking is SHARED, so no category filter
        url += `&category=${category}`;
      }
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setAvailableExams(data);
    } catch (error) {
      console.error('Failed to fetch exams:', error);
    }
  };

  const fetchMocksForExport = async (category = '') => {
    try {
      const url = category 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/mocks?category=${category}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/mocks`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setAvailableMocks(data);
    } catch (error) {
      console.error('Failed to fetch mocks:', error);
    }
  };

  const handleExportPartialCSV = async () => {
    setExportingCSV(true);
    try {
      const params = {};
      if (exportFilters.module) params.module = exportFilters.module;
      if (exportFilters.exam_id) params.exam_id = exportFilters.exam_id;
      if (exportFilters.category) params.category = exportFilters.category;
      
      const response = await adminAPI.exportPartialCSV(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `partial_bookings_${format(new Date(), 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Partial bookings exported!');
      setShowPartialExportModal(false);
      setExportFilters({ module: '', exam_id: '', category: '', mock_id: '' });
    } catch (error) {
      console.error('Export error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Export failed';
      toast.error(errorMsg);
    } finally {
      setExportingCSV(false);
    }
  };

  const handleExportMockCSV = async () => {
    setExportingCSV(true);
    try {
      const params = {};
      if (exportFilters.mock_id) params.mock_id = exportFilters.mock_id;
      if (exportFilters.category) params.category = exportFilters.category;
      
      const response = await adminAPI.exportMockCSV(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mock_bookings_${format(new Date(), 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Mock bookings exported!');
      setShowMockExportModal(false);
      setExportFilters({ module: '', exam_id: '', category: '', mock_id: '' });
    } catch (error) {
      console.error('Export error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Export failed';
      toast.error(errorMsg);
    } finally {
      setExportingCSV(false);
    }
  };

  const handleAdjustCredits = async (userId, delta, reason) => {
    try {
      await adminAPI.adjustCredits(userId, delta, reason);
      toast.success(`Credits adjusted: ${delta > 0 ? '+' : ''}${delta}`);
      fetchStudents();
    } catch (error) {
      toast.error('Failed to adjust credits');
    }
  };

  const fetchAvailableSlots = async () => {
    // For batch students, require selection. For other students, always allow fetch
    if (studentType === 'batch' && !selectedStudent) return;
    
    // For other students, require category selection EXCEPT for Speaking (which is SHARED)
    if (studentType === 'other' && !manualBookingFilters.category) {
      // Speaking module doesn't need category (SHARED category)
      if (manualBookingType === 'partial' && manualBookingFilters.module === 'SPEAKING') {
        // Allow Speaking without category
      } else {
        setAvailableSlots([]);
        return;
      }
    }
    
    setLoadingManualSlots(true);
    try {
      if (manualBookingType === 'partial') {
        const params = { time_filter: 'active' };  // Only show active/future exams
        if (manualBookingFilters.module) params.module = manualBookingFilters.module;
        if (manualBookingFilters.exam_date) params.exam_date = manualBookingFilters.exam_date;
        // Don't send category for Speaking module (it's SHARED)
        if (manualBookingFilters.category && manualBookingFilters.module !== 'SPEAKING') {
          params.category = manualBookingFilters.category;
        }
        
        const response = await adminAPI.getAvailablePartialSlots(params);
        setAvailableSlots(response.data.filter(slot => slot.remaining_seats > 0));
      } else {
        // Mock exams - only show active/future exams
        const params = { time_filter: 'active' };
        if (manualBookingFilters.category) params.category = manualBookingFilters.category;
        
        const response = await adminAPI.getAvailableMockSlots(params);
        setAvailableSlots(response.data.filter(mock => mock.lrw_remaining_seats > 0));
      }
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      toast.error('Failed to load available slots');
    } finally {
      setLoadingManualSlots(false);
    }
  };

  useEffect(() => {
    if (showManualBooking) {
      // For batch students: fetch when student is selected
      // For other students: fetch immediately when exam type/filters change
      if (studentType === 'batch' && selectedStudent) {
        fetchAvailableSlots();
      } else if (studentType === 'other') {
        fetchAvailableSlots();
      }
    }
  }, [manualBookingType, manualBookingFilters, selectedStudent, studentType]);


  useEffect(() => {
    if (showSpeakingSlotModal && selectedMockForSpeaking) {
      fetchAvailableSpeakingSlots(selectedMockForSpeaking.id);
    }
  }, [showSpeakingSlotModal, selectedMockForSpeaking]);


  const handleManualBooking = async () => {
    // Validation
    if (studentType === 'batch' && (!selectedStudent || selectedStudent.trim() === '')) {
      toast.error('Please select a student');
      return;
    }
    
    if (studentType === 'other') {
      if (!otherStudentData.name || !otherStudentData.batch_no || !otherStudentData.candidate_no) {
        toast.error('Please fill all student details (Name, Batch No, Candidate No)');
        return;
      }
      
      // Category is required for other students (both partial and mock)
      if (!manualBookingFilters.category) {
        toast.error('Please select exam category (CD or PB)');
        return;
      }
    }
    
    if (!selectedSlot) {
      toast.error('Please select a slot');
      return;
    }

    setBookingInProgress(true);
    try {
      const bookingData = {
        slot_id: selectedSlot,
        is_other_student: studentType === 'other'
      };
      
      if (studentType === 'batch') {
        bookingData.user_id = selectedStudent;
        // Add previous mock info for batch students with CD category (for mock bookings)
        // Note: radiance_mock_number is auto-calculated by backend for registered students
        if (manualBookingType === 'mock') {
          const student = students.find(s => s.id === selectedStudent);
          if (student?.exam_category === 'CD') {
            bookingData.previous_mock_at_radiance = batchStudentPreviousMock;
          }
        }
      } else {
        bookingData.other_student_name = otherStudentData.name;
        bookingData.other_student_batch = otherStudentData.batch_no;
        bookingData.other_student_candidate_no = otherStudentData.candidate_no;
        bookingData.other_student_phone = otherStudentData.phone;
        bookingData.other_student_email = otherStudentData.email;
        // Validate: email is required for MOCK bookings (for confirmation email)
        if (manualBookingType === 'mock') {
          const em = (otherStudentData.email || '').trim();
          if (!em) {
            toast.error('Email is required for mock bookings');
            setBookingInProgress(false);
            return;
          }
          if (!em.includes('@') || !em.split('@')[1]?.includes('.')) {
            toast.error('Please enter a valid email address');
            setBookingInProgress(false);
            return;
          }
        }
        // Add previous mock info for CD category (for mock bookings)
        // For other students, admin must input the mock number manually
        if (manualBookingType === 'mock' && manualBookingFilters.category === 'CD') {
          bookingData.previous_mock_at_radiance = otherStudentData.previous_mock_at_radiance;
          if (otherStudentData.previous_mock_at_radiance === true && otherStudentData.radiance_mock_number) {
            bookingData.radiance_mock_number = parseInt(otherStudentData.radiance_mock_number);
          }
        }
      }
      
      if (manualBookingType === 'partial') {
        // Call admin manual booking API with new payload
        await adminAPI.manualBookPartial(bookingData);
        toast.success('Partial exam booked successfully!');
      } else {
        // Call admin manual mock booking API with new payload
        await adminAPI.manualBookMock(bookingData);
        toast.success('Mock test booked successfully!');
      }
      
      // Notify other components about new booking
      window.dispatchEvent(new Event('bookingCreated'));
      
      setShowManualBooking(false);
      setStudentType('batch');
      setSelectedStudent('');
      setBatchStudentPreviousMock(null);
      setBatchStudentMockNumber('');  // Reset batch student mock number
      setOtherStudentData({ name: '', batch_no: '', candidate_no: '', phone: '', email: '', previous_mock_at_radiance: null, radiance_mock_number: '' });
      setSelectedSlot('');
      setAvailableSlots([]);
      setManualBookingFilters({ module: 'LISTENING', exam_date: '', category: '' });
      fetchStats();
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to book slot');
    } finally {
      setBookingInProgress(false);
    }
  };


  const fetchAvailableSpeakingSlots = async (mockId) => {
    setLoadingSpeakingSlots(true);
    try {
      const response = await adminAPI.getMockSpeakingSlots(mockId);
      // Show all slots - admin can book any slot (is_available may not be set in database)
      setAvailableSpeakingSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch speaking slots:', error);
      toast.error('Failed to load speaking slots');
      setAvailableSpeakingSlots([]);
    } finally {
      setLoadingSpeakingSlots(false);
    }
  };

  const handleBookMockWithSpeaking = async () => {
    // Validate student selection for batch students
    if (studentType === 'batch' && (!selectedStudent || selectedStudent.trim() === '')) {
      toast.error('Please select a student');
      return;
    }
    
    // Validate other student details
    if (studentType === 'other') {
      if (!otherStudentData.name || !otherStudentData.batch_no || !otherStudentData.candidate_no) {
        toast.error('Please fill all student details (Name, Batch No, Candidate No)');
        return;
      }
    }
    
    if (!selectedSpeakingSlot) {
      toast.error('Please select a speaking slot');
      return;
    }
    
    // Validate WhatsApp number if online mode
    if (manualSpeakingMode === 'ONLINE') {
      const cleanNumber = manualWhatsappNumber.replace(/\D/g, '');
      if (!manualWhatsappNumber || cleanNumber.length !== 11) {
        toast.error('WhatsApp number must be exactly 11 digits for online mode');
        return;
      }
    }

    setBookingInProgress(true);
    try {
      const bookingData = {
        slot_id: selectedSlot,
        speaking_slot_id: selectedSpeakingSlot,
        is_other_student: studentType === 'other',
        speak_mode: manualSpeakingMode,
        whatsapp_number: manualSpeakingMode === 'ONLINE' ? manualWhatsappNumber : null
      };
      
      if (studentType === 'batch') {
        bookingData.user_id = selectedStudent;
        // Add previous mock info for batch students with CD category
        // Note: radiance_mock_number is auto-calculated by backend for registered students
        const student = students.find(s => s.id === selectedStudent);
        if (student?.exam_category === 'CD') {
          bookingData.previous_mock_at_radiance = batchStudentPreviousMock;
        }
      } else {
        bookingData.other_student_name = otherStudentData.name;
        bookingData.other_student_batch = otherStudentData.batch_no;
        bookingData.other_student_candidate_no = otherStudentData.candidate_no;
        bookingData.other_student_phone = otherStudentData.phone;
        bookingData.other_student_email = otherStudentData.email;
        // Email mandatory for mock bookings (confirmation email goes here)
        {
          const em = (otherStudentData.email || '').trim();
          if (!em || !em.includes('@') || !em.split('@')[1]?.includes('.')) {
            toast.error('Valid email is required for mock bookings');
            return;
          }
        }
        // Add previous mock info for CD category
        // For other students, admin must input the mock number manually
        if (manualBookingFilters.category === 'CD') {
          bookingData.previous_mock_at_radiance = otherStudentData.previous_mock_at_radiance;
          if (otherStudentData.previous_mock_at_radiance === true && otherStudentData.radiance_mock_number) {
            bookingData.radiance_mock_number = parseInt(otherStudentData.radiance_mock_number);
          }
        }
      }

      await adminAPI.manualBookMock(bookingData);
      toast.success('Mock test booked successfully with speaking slot!');
      
      // Notify and reset
      window.dispatchEvent(new Event('bookingCreated'));
      setShowSpeakingSlotModal(false);
      setShowManualBooking(false);
      setStudentType('batch');
      setSelectedStudent('');
      setBatchStudentPreviousMock(null);  // Reset batch student previous mock
      setOtherStudentData({ name: '', batch_no: '', candidate_no: '', phone: '', email: '', previous_mock_at_radiance: null, radiance_mock_number: '' });
      setSelectedSlot('');
      setSelectedSpeakingSlot('');
      setSelectedMockForSpeaking(null);
      setAvailableSlots([]);
      setAvailableSpeakingSlots([]);
      setManualSpeakingMode('OFFLINE');
      setManualWhatsappNumber('');
      setManualBookingFilters({ module: 'LISTENING', exam_date: '', category: '' });
      fetchStats();
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to book mock slot');
    } finally {
      setBookingInProgress(false);
    }
  };

  // handleCreateExam moved to CreateExamModal component
  const handleExamCreated = () => {
    fetchStats();
    fetchExams();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white shadow-xl">
        <div className="container mx-auto px-3 md:px-6 py-4 md:py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <TrendingUp className="w-8 h-8" />
                Admin Dashboard
              </h1>
              <p className="text-blue-100 mt-1">Radiance Education</p>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <Badge className="bg-white/20 backdrop-blur-lg px-2 md:px-4 py-1 md:py-2 text-sm md:text-lg">
                <span className="hidden sm:inline">{user?.name}</span>
                <UserIcon className="w-4 h-4 sm:hidden" />
              </Badge>
              <Button 
                variant="outline"
                onClick={() => setShowPasswordChange(true)}
                size="sm"
                className="bg-white/20 border-white/40 text-white hover:bg-white/30 hidden md:flex"
                title="Change Password"
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowPasswordChange(true)}
                size="sm"
                className="bg-white/20 border-white/40 text-white hover:bg-white/30 md:hidden"
                title="Change Password"
              >
                <Lock className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                onClick={logout} 
                size="sm"
                className="bg-white/20 border-white/40 text-white hover:bg-white/30"
              >
                <LogOut className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-3 md:px-6 py-4 md:py-8">
        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="grid w-full max-w-4xl mx-auto grid-cols-5 mb-4 md:mb-8 h-auto">
            <TabsTrigger value="dashboard" className="text-xs md:text-sm py-2 md:py-3">
              <span className="hidden sm:inline">Dashboard</span>
              <span className="sm:hidden">Home</span>
            </TabsTrigger>
            <TabsTrigger value="exams" className="text-xs md:text-sm py-2 md:py-3">
              <Calendar className="w-3 h-3 md:w-4 md:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Exams</span>
            </TabsTrigger>
            <TabsTrigger value="batches" className="text-xs md:text-sm py-2 md:py-3">
              <Layers className="w-3 h-3 md:w-4 md:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Batches</span>
            </TabsTrigger>
            <TabsTrigger value="students" className="text-xs md:text-sm py-2 md:py-3">
              <Users className="w-3 h-3 md:w-4 md:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Students</span>
            </TabsTrigger>
            {/* Mods tab visible for both admin and moderator */}
            <TabsTrigger value="moderators" className="text-xs md:text-sm py-2 md:py-3">
              <Shield className="w-3 h-3 md:w-4 md:h-4 sm:mr-2" />
              <span className="hidden sm:inline">{user?.role === 'admin' ? 'Mods' : 'Tools'}</span>
            </TabsTrigger>
            {/* External tab — Admin always; Moderator only if granted */}
            {(user?.role === 'admin' || user?.can_manage_external_bookings) && (
              <TabsTrigger value="external-approval" className="text-xs md:text-sm py-2 md:py-3">
                <span className="hidden sm:inline">External</span>
                <span className="sm:hidden">Ext</span>
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="dashboard">
            {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
          <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0 hover:shadow-xl transition-shadow">
            <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600">Total Students</p>
                  <p className="text-xl md:text-3xl font-bold text-blue-600" data-testid="total-students">
                    {stats?.total_students || 0}
                  </p>
                </div>
                <Users className="w-8 h-8 md:w-12 md:h-12 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0 hover:shadow-xl transition-shadow">
            <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600">Active Partial Tests</p>
                  <p className="text-xl md:text-3xl font-bold text-purple-600">
                    {stats?.total_active_partial || 0}
                  </p>
                </div>
                <BookOpen className="w-8 h-8 md:w-12 md:h-12 text-purple-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0 hover:shadow-xl transition-shadow">
            <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600">Active Mock Tests</p>
                  <p className="text-xl md:text-3xl font-bold text-pink-600">
                    {stats?.total_active_mock || 0}
                  </p>
                </div>
                <Calendar className="w-8 h-8 md:w-12 md:h-12 text-pink-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="backdrop-blur-lg bg-white/80 shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer"
            onClick={() => setShowDoneExamsModal(true)}
          >
            <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600">Done Exams</p>
                  <p className="text-xl md:text-3xl font-bold text-green-600">
                    {stats?.total_completed_exams || 0}
                  </p>
                  <p className="text-[10px] md:text-xs text-gray-400 mt-1">Click for details</p>
                </div>
                <TrendingUp className="w-8 h-8 md:w-12 md:h-12 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          {/* Done Exams Modal */}
          <Dialog open={showDoneExamsModal} onOpenChange={setShowDoneExamsModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Done Exams Breakdown
                </DialogTitle>
                <DialogDescription>
                  Total completed exams by category
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📅</span>
                    <div>
                      <p className="font-medium text-purple-800">Speaking</p>
                      <p className="text-xs text-purple-600">Unique dates (not slots)</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-purple-600">
                    {stats?.completed_breakdown?.speaking_dates || 0}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📝</span>
                    <div>
                      <p className="font-medium text-blue-800">Partial Tests</p>
                      <p className="text-xs text-blue-600">Reading, Writing, Listening slots</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">
                    {stats?.completed_breakdown?.other_partial_slots || 0}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🎯</span>
                    <div>
                      <p className="font-medium text-orange-800">Mock Tests</p>
                      <p className="text-xs text-orange-600">Full mock exams</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-orange-600">
                    {stats?.completed_breakdown?.mock_exams || 0}
                  </span>
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">✅</span>
                      <p className="font-bold text-green-800">Total Done</p>
                    </div>
                    <span className="text-3xl font-bold text-green-600">
                      {stats?.total_completed_exams || 0}
                    </span>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Quick Actions */}
        <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0 mb-4 md:mb-8">
          <CardHeader className="pb-3 md:pb-6">
            <CardTitle className="text-lg md:text-xl">Quick Actions</CardTitle>
            <CardDescription className="text-xs md:text-sm">Manage exams, bookings and exports</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4">
              <Button 
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-xs md:text-sm"
                size="sm"
                onClick={() => setShowCreateExam(true)}
                data-testid="create-exam-button"
              >
                <Calendar className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                <span className="hidden md:inline">Create Exam</span>
                <span className="md:hidden ml-1">Exam</span>
              </Button>
              <Button 
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-xs md:text-sm"
                size="sm"
                onClick={() => setShowManualBooking(true)}
                data-testid="manual-booking-button"
              >
                <UserPlus className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                <span className="hidden md:inline">Manual Booking</span>
                <span className="md:hidden ml-1">Book</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowPartialExportModal(true);
                  setExportFilters({ module: '', exam_id: '', category: '', mock_id: '' });
                }}
                data-testid="export-partial-button"
              >
                <FileDown className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Export Partial</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  setShowMockExportModal(true);
                  fetchMocksForExport();
                  setExportFilters({ module: '', exam_id: '', category: '', mock_id: '' });
                }}
                data-testid="export-mock-button"
              >
                <FileDown className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Export Mock</span>
              </Button>
              
              {/* Fix Cancelled Slots - Admin only */}
              {isAdmin && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleFixCancelledSlots}
                  disabled={fixingOccupancy}
                  className="bg-orange-600 hover:bg-orange-700 text-xs md:text-sm"
                >
                  <AlertCircle className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                  <span className="hidden md:inline">{fixingOccupancy ? 'Fixing...' : 'Fix Cancelled Slots'}</span>
                  <span className="md:hidden ml-1">{fixingOccupancy ? '...' : 'Fix'}</span>
                </Button>
              )}
            </div>
            
            {occupancyFixResult && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">✅ Fix Complete</h4>
                <p className="text-sm text-green-700">
                  Checked: {occupancyFixResult.checked} slots | Fixed: {occupancyFixResult.fixed} mismatches
                </p>
                {occupancyFixResult.details && occupancyFixResult.details.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-sm font-medium text-green-800">View Details</summary>
                    <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                      {occupancyFixResult.details.map((detail, idx) => (
                        <div key={idx} className="text-xs bg-white p-2 rounded border">
                          <strong>{detail.module}</strong> - {detail.date} {detail.time}
                          <br />
                          Before: {detail.before_taken} → After: {detail.actual_booked}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Bookings & Cancellations - Inside Dashboard Tab */}
        <RecentBookingsSection />
          </TabsContent>

          <TabsContent value="exams">
            {/* Active/Past Exams Tabs */}
            <div className="mb-6">
              <Tabs value={examTabType} onValueChange={setExamTabType} className="w-full">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                  <TabsTrigger value="active">Active Exams</TabsTrigger>
                  <TabsTrigger value="past">Past Exams</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            {/* Exams Listing */}
            <div className="space-y-6">
              {/* Partial Exams */}
              <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <CardTitle>Partial Exams</CardTitle>
                      <CardDescription>Individual module exams (L/R/W/S)</CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Module Filter */}
                      <Select
                        value={partialExamModuleFilter}
                        onValueChange={setPartialExamModuleFilter}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Filter by Module" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ALL">All Modules</SelectItem>
                          <SelectItem value="LISTENING">Listening</SelectItem>
                          <SelectItem value="READING">Reading</SelectItem>
                          <SelectItem value="WRITING">Writing</SelectItem>
                          <SelectItem value="SPEAKING">Speaking</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {selectedPartialExams.length > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleBulkDeletePartial}
                        >
                          Delete Selected ({selectedPartialExams.length})
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Always show date-based view with "View Slots" button for all modules */}
                  <PartialExamsDateView moduleFilter={partialExamModuleFilter} timeFilter={examTabType} />
                </CardContent>
              </Card>

              {/* Mock Exams */}
              <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Mock Tests</CardTitle>
                      <CardDescription>Full LRW + Speaking mock exams</CardDescription>
                    </div>
                    {selectedMockExams.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleBulkDeleteMock}
                      >
                        Delete Selected ({selectedMockExams.length})
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingExams ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  ) : mockExams.length === 0 ? (
                    <div className="text-center py-8 text-gray-600">
                      No mock tests created yet
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {mockExams.map((mock) => (
                        <div 
                          key={mock.id}
                          className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <input
                              type="checkbox"
                              checked={selectedMockExams.includes(mock.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMockExams([...selectedMockExams, mock.id]);
                                } else {
                                  setSelectedMockExams(selectedMockExams.filter(id => id !== mock.id));
                                }
                              }}
                              className="w-4 h-4 cursor-pointer"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <Badge className="bg-purple-600">{mock.mock_uid}</Badge>
                                {mock.category && (
                                  <Badge 
                                    variant={mock.category === 'PB' ? 'default' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {mock.category}
                                  </Badge>
                                )}
                                {mock.external_only && (
                                  <Badge className="text-xs bg-amber-500 hover:bg-amber-500 text-white">
                                    EXTERNAL ONLY
                                  </Badge>
                                )}
                                <span className="font-semibold">{formatDate(mock.lrw_date)}</span>
                                <span className="text-sm text-gray-600">{formatTimeRange(mock.lrw_start, mock.lrw_end)}</span>
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                {mock.campus && `Campus: ${mock.campus}`}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-center">
                              <div className="text-sm text-gray-600">LRW Seats</div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                                  onClick={async () => {
                                    try {
                                      await adminAPI.decreaseMockSeats(mock.id);
                                      toast.success('Seat decreased successfully');
                                      fetchMocksForExport();
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Failed to decrease seats');
                                    }
                                  }}
                                  disabled={mock.lrw_total_seats <= 1}
                                >
                                  -
                                </Button>
                                <div className={`text-xl font-bold min-w-[60px] text-center ${mock.lrw_remaining_seats > 5 ? 'text-green-600' : 'text-red-600'}`}>
                                  {mock.lrw_remaining_seats}/{mock.lrw_total_seats}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                                  onClick={async () => {
                                    try {
                                      await adminAPI.increaseMockSeats(mock.id);
                                      toast.success('Seat increased successfully');
                                      fetchMocksForExport();
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Failed to increase seats');
                                    }
                                  }}
                                >
                                  +
                                </Button>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedMockForSlots(mock);
                                  setShowSpeakingSlotsModal(true);
                                  fetchSpeakingSlots(mock.id);
                                }}
                              >
                                View Slots
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteMockExam(mock.id)}
                              >
                                🗑️
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="batches">
            <BatchManagement userRole={user?.role} />
          </TabsContent>

          <TabsContent value="students">
            {/* Students Management */}
            <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0">
              <div>
                <CardTitle className="text-lg md:text-xl">Students Management</CardTitle>
                <CardDescription className="text-xs md:text-sm">View and manage student accounts</CardDescription>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <Label htmlFor="batch-filter" className="text-xs md:text-sm font-medium whitespace-nowrap">Batch:</Label>
                <Select value={selectedBatchFilter} onValueChange={setSelectedBatchFilter}>
                  <SelectTrigger className="w-full md:w-[200px]" id="batch-filter">
                    <SelectValue placeholder="All Batches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Batches</SelectItem>
                    {batches.map((batch) => (
                      <SelectItem key={batch.id} value={batch.name}>
                        {batch.name} ({batch.total_students})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                <p className="mt-4 text-gray-600">Loading students...</p>
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <p className="text-gray-600">No students found</p>
                <p className="text-sm text-gray-500 mt-2">
                  {selectedBatchFilter !== 'all' ? 'Try selecting a different batch' : 'Create batches and add students to get started'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {students.map((student) => (
                  <div 
                    key={student.id} 
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 md:p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg hover:shadow-md transition-shadow gap-3"
                  >
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => {
                        setSelectedStudentForProfile(student);
                        setShowStudentProfile(true);
                      }}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-sm md:text-base">
                          {student.name}
                          {student.candidate_no && (
                            <span className="ml-2 text-xs text-gray-500 font-normal">#{student.candidate_no}</span>
                          )}
                        </div>
                        {student.is_active !== true && (
                          <Badge variant="destructive" className="text-xs">Paused</Badge>
                        )}
                        {student.exam_category && (
                          <Badge 
                            variant={student.exam_category === 'PB' ? 'default' : 'secondary'} 
                            className="text-xs"
                          >
                            {student.exam_category}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs md:text-sm text-gray-600 truncate">{student.email}</div>
                      {student.batch_code && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          {student.batch_code}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-center">
                      {(isAdmin || isModerator) && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className={`${student.is_active !== true ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'} text-xs md:text-sm`}
                            onClick={() => handleToggleStudentActive(student.id, student.name, student.is_active === true)}
                            title={student.is_active !== true ? 'Resume Login' : 'Pause Login'}
                          >
                            {student.is_active !== true ? '▶️' : '⏸️'}
                          </Button>
                          {isAdmin && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs md:text-sm"
                              onClick={() => handleDeleteStudent(student.id, student.name)}
                              title="Delete Student"
                            >
                              🗑️
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Moderators/Tools Tab */}
          <TabsContent value="moderators">
            <Tabs defaultValue={user?.role === 'admin' ? 'management' : 'email-queue'} className="w-full">
              <TabsList className="w-full flex flex-wrap gap-1 h-auto p-1 bg-gray-100/80">
                {/* Moderator Management - Admin only */}
                {user?.role === 'admin' && (
                  <TabsTrigger value="management" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Moderators</TabsTrigger>
                )}
                {/* Activity Log - Admin only */}
                {user?.role === 'admin' && (
                  <TabsTrigger value="activity-log" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Activity Log</TabsTrigger>
                )}
                {/* Messages - Both Admin & Moderator */}
                <TabsTrigger value="messages" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">💬 Messages</TabsTrigger>
                {/* Export Numbers - Admin only */}
                {isAdmin && (
                  <TabsTrigger value="export-numbers" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">📱 Export</TabsTrigger>
                )}
                {/* Email Queue - Both Admin & Moderator */}
                <TabsTrigger value="email-queue" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">Email Queue</TabsTrigger>
                {/* Email Notifications - Both Admin & Moderator */}
                <TabsTrigger value="email-notifications" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">📧 Notifications</TabsTrigger>
                {/* Notices - Admin or Moderator with can_manage_notices permission */}
                {(isAdmin || user?.can_manage_notices) && (
                  <TabsTrigger value="notices" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">
                    <Bell className="h-3 w-3 mr-1 inline" />
                    Notices
                  </TabsTrigger>
                )}
                {/* System Tools - Admin only */}
                {user?.role === 'admin' && (
                  <TabsTrigger value="system-tools" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5">⚙️ Tools</TabsTrigger>
                )}
              </TabsList>
              
              {user?.role === 'admin' && (
                <TabsContent value="management">
                  <ModeratorsManagement />
                </TabsContent>
              )}
              
              {user?.role === 'admin' && (
                <TabsContent value="activity-log">
                  <AdminActivityLog />
                </TabsContent>
              )}
              
              <TabsContent value="messages">
                <MessagesManagement />
              </TabsContent>
              
              {/* Export Numbers - Admin only */}
              {isAdmin && (
                <TabsContent value="export-numbers">
                  <ExportNumbers />
                </TabsContent>
              )}
              
              <TabsContent value="email-queue">
                <EmailQueueManager />
              </TabsContent>
              
              <TabsContent value="email-notifications">
                <EmailNotificationSender />
              </TabsContent>
              
              {/* Notices - Admin or Moderator with permission */}
              {(isAdmin || user?.can_manage_notices) && (
                <TabsContent value="notices">
                  <NoticeManagement />
                </TabsContent>
              )}
              
              {user?.role === 'admin' && (
                <TabsContent value="system-tools">
                  <SystemTools />
                </TabsContent>
              )}
            </Tabs>
          </TabsContent>

          {/* External Approval Tab — Admin always; Moderator only if granted */}
          {(user?.role === 'admin' || user?.can_manage_external_bookings) && (
            <TabsContent value="external-approval">
              <ExternalApproval />
            </TabsContent>
          )}
        </Tabs>
      </div>


      {/* Create Exam Modal */}
      <CreateExamModal 
        open={showCreateExam} 
        onOpenChange={setShowCreateExam} 
        onExamCreated={handleExamCreated}
      />

      {/* Manual Booking Modal */}
      <Dialog open={showManualBooking} onOpenChange={setShowManualBooking}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manual Booking</DialogTitle>
            <DialogDescription>
              Book a slot for a student manually (No credit deduction for Other Students)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Student Type Selector */}
            <div className="space-y-2">
              <Label>Student Type *</Label>
              <Select value={studentType} onValueChange={(val) => {
                setStudentType(val);
                setSelectedStudent('');
                setOtherStudentData({ name: '', batch_no: '', candidate_no: '', phone: '', email: '', previous_mock_at_radiance: null, radiance_mock_number: '' });
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="batch">Select From Batch</SelectItem>
                  <SelectItem value="other">Other Student (Manual Entry)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Batch Student Selector - Searchable */}
            {studentType === 'batch' && (
              <div className="space-y-2">
                <Label>Select Student *</Label>
                <Popover open={studentSearchOpen} onOpenChange={(open) => {
                  setStudentSearchOpen(open);
                  if (open) setStudentSearchQuery(''); // Reset search when opening
                }}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={studentSearchOpen}
                      className="w-full justify-between font-normal"
                    >
                      {selectedStudent ? (() => {
                        const student = students.find(s => s.id === selectedStudent);
                        return student ? (
                          <span className="truncate">
                            {student.name} 
                            <span className="text-muted-foreground ml-1">
                              [{student.batch_code || 'No Batch'}]
                            </span>
                          </span>
                        ) : "Select student...";
                      })() : "Search student by name, batch, or email..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput 
                        placeholder="Search by name, batch code, or email..." 
                        value={studentSearchQuery}
                        onValueChange={setStudentSearchQuery}
                      />
                      <CommandList className="max-h-[300px]">
                        <CommandEmpty>No student found.</CommandEmpty>
                        <CommandGroup heading={`Students (${filteredStudents.length})`}>
                          {filteredStudents.map((student) => (
                            <CommandItem
                              key={student.id}
                              value={student.id}
                              onSelect={() => {
                                setSelectedStudent(student.id);
                                setStudentSearchOpen(false);
                                // Auto-set category based on selected student
                                if (student.exam_category) {
                                  setManualBookingFilters({
                                    ...manualBookingFilters, 
                                    category: student.exam_category
                                  });
                                }
                              }}
                              className="cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedStudent === student.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{student.name}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {student.batch_code || 'No Batch'}
                                  </Badge>
                                  {student.exam_category && (
                                    <Badge variant="secondary" className="text-xs">
                                      {student.exam_category}
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {student.email} • Credits: {student.credit_balance || 0}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {selectedStudent && (() => {
                  const student = students.find(s => s.id === selectedStudent);
                  return student?.exam_category && (
                    <p className="text-sm text-blue-600">
                      ℹ️ Student Category: <strong>{student.exam_category}</strong> - Showing only {student.exam_category} exams
                    </p>
                  );
                })()}
                
                {/* Previous Mock at Radiance - For Batch Students with CD + Mock Test */}
                {selectedStudent && manualBookingType === 'mock' && (() => {
                  const student = students.find(s => s.id === selectedStudent);
                  if (student?.exam_category !== 'CD') return null;
                  return (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mt-3">
                      <Label className="text-amber-800 font-semibold mb-3 block">
                        আগে mock দিসেন? *
                      </Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="batch_previous_mock"
                            checked={batchStudentPreviousMock === true}
                            onChange={() => setBatchStudentPreviousMock(true)}
                            className="w-4 h-4 text-amber-600"
                          />
                          <span className="text-gray-700">Yes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="batch_previous_mock"
                            checked={batchStudentPreviousMock === false}
                            onChange={() => {
                              setBatchStudentPreviousMock(false);
                            }}
                            className="w-4 h-4 text-amber-600"
                          />
                          <span className="text-gray-700">No</span>
                        </label>
                      </div>
                      
                      {/* Info: Mock number is auto-calculated for registered students */}
                      <p className="mt-3 text-xs text-green-700 bg-green-50 p-2 rounded">
                        ✅ Mock Number automatically calculate হবে student এর booking history থেকে
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Other Student Manual Entry */}
            {studentType === 'other' && (
              <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700 font-medium">
                  ℹ️ Enter student details manually (Not registered in portal)
                </p>
                <div className="space-y-2">
                  <Label htmlFor="other-name">Student Name *</Label>
                  <Input 
                    id="other-name"
                    placeholder="Enter full name"
                    value={otherStudentData.name}
                    onChange={(e) => setOtherStudentData({...otherStudentData, name: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="other-batch">Batch No *</Label>
                    <Input 
                      id="other-batch"
                      placeholder="e.g., DEMO2025"
                      value={otherStudentData.batch_no}
                      onChange={(e) => setOtherStudentData({...otherStudentData, batch_no: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="other-candidate">Candidate No *</Label>
                    <Input 
                      id="other-candidate"
                      placeholder="e.g., 025846"
                      value={otherStudentData.candidate_no}
                      onChange={(e) => setOtherStudentData({...otherStudentData, candidate_no: e.target.value})}
                    />
                  </div>
                </div>
                {/* NEW: Phone Number Field */}
                <div className="space-y-2">
                  <Label htmlFor="other-phone">Phone Number</Label>
                  <Input 
                    id="other-phone"
                    type="tel"
                    placeholder="e.g., 01712345678"
                    value={otherStudentData.phone}
                    onChange={(e) => setOtherStudentData({...otherStudentData, phone: e.target.value})}
                  />
                </div>
                
                {/* Email Field — required for mock, optional for partial */}
                <div className="space-y-2">
                  <Label htmlFor="other-email">
                    Email {manualBookingType === 'mock' ? <span className="text-red-500">*</span> : '(Optional)'}
                  </Label>
                  <Input 
                    id="other-email"
                    data-testid="other-email-input"
                    type="email"
                    placeholder="e.g., student@example.com"
                    value={otherStudentData.email}
                    onChange={(e) => setOtherStudentData({...otherStudentData, email: e.target.value})}
                    required={manualBookingType === 'mock'}
                  />
                </div>
                
                {/* Exam Category for Other Students */}
                <div className="space-y-2">
                  <Label htmlFor="other-category">
                    Exam Category {manualBookingType === 'partial' && manualBookingFilters.module === 'SPEAKING' ? '(Not Required for Speaking)' : '*'}
                  </Label>
                  <Select 
                    value={manualBookingFilters.category} 
                    onValueChange={(val) => setManualBookingFilters({...manualBookingFilters, category: val})}
                    disabled={manualBookingType === 'partial' && manualBookingFilters.module === 'SPEAKING'}
                  >
                    <SelectTrigger id="other-category">
                      <SelectValue placeholder={manualBookingType === 'partial' && manualBookingFilters.module === 'SPEAKING' ? 'SHARED (All Categories)' : 'Select CD or PB'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CD">CD (Computer Delivered)</SelectItem>
                      <SelectItem value="PB">PB (Paper Based)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Previous Mock at Radiance - Only for CD + Mock Test */}
                {manualBookingFilters.category === 'CD' && manualBookingType === 'mock' && (
                  <div className="col-span-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label className="text-amber-800 font-semibold mb-3 block">
                      Has this student given a previous Mock Test at Radiance? *
                    </Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="previous_mock"
                          checked={otherStudentData.previous_mock_at_radiance === true}
                          onChange={() => setOtherStudentData({...otherStudentData, previous_mock_at_radiance: true})}
                          className="w-4 h-4 text-amber-600"
                        />
                        <span className="text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="previous_mock"
                          checked={otherStudentData.previous_mock_at_radiance === false}
                          onChange={() => setOtherStudentData({...otherStudentData, previous_mock_at_radiance: false, radiance_mock_number: ''})}
                          className="w-4 h-4 text-amber-600"
                        />
                        <span className="text-gray-700">No</span>
                      </label>
                    </div>
                    
                    {/* Show mock number input when Yes is selected */}
                    {otherStudentData.previous_mock_at_radiance === true && (
                      <div className="mt-3 p-3 bg-white rounded border border-amber-300">
                        <Label className="text-amber-700 text-sm mb-2 block">
                          Radiance এ এটা কততম Mock? *
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          max="99"
                          placeholder="যেমন: 2 (এর আগে 1টা দিয়ে থাকলে)"
                          value={otherStudentData.radiance_mock_number}
                          onChange={(e) => setOtherStudentData({...otherStudentData, radiance_mock_number: e.target.value})}
                          className="w-32"
                        />
                        <p className="text-xs text-amber-600 mt-1">
                          এই student আগে Radiance এ কয়টা mock দিয়েছে সেটার পরের নম্বর লিখুন
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Exam Type Selector */}
            <div className="space-y-2">
              <Label>Exam Type *</Label>
              <Select value={manualBookingType} onValueChange={setManualBookingType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="partial">Partial Exam</SelectItem>
                  <SelectItem value="mock">Mock Test</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filters for Partial Exams */}
            {manualBookingType === 'partial' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Module</Label>
                  <Select 
                    value={manualBookingFilters.module} 
                    onValueChange={(val) => setManualBookingFilters({...manualBookingFilters, module: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LISTENING">Listening</SelectItem>
                      <SelectItem value="READING">Reading</SelectItem>
                      <SelectItem value="WRITING">Writing</SelectItem>
                      <SelectItem value="SPEAKING">Speaking</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input 
                    type="date" 
                    value={manualBookingFilters.exam_date}
                    onChange={(e) => setManualBookingFilters({...manualBookingFilters, exam_date: e.target.value})}
                  />
                </div>
              </div>
            )}

            {/* Available Slots */}
            {((studentType === 'batch' && selectedStudent) || 
              (studentType === 'other' && (manualBookingFilters.category || 
                (manualBookingType === 'partial' && manualBookingFilters.module === 'SPEAKING')))) && (
              <div className="space-y-2">
                <Label>Select Slot *</Label>
                {loadingManualSlots ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    <p className="text-sm text-gray-600 mt-2">Loading slots...</p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-4 text-gray-600">
                    No available slots found. Try different filters.
                  </div>
                ) : (
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {availableSlots.map((slot) => (
                      <div 
                        key={slot.id}
                        onClick={() => {
                          if (manualBookingType === 'partial') {
                            // For partial, direct selection
                            setSelectedSlot(slot.id);
                          } else {
                            // For mock, open speaking slot modal
                            setSelectedMockForSpeaking(slot);
                            setSelectedSlot(slot.id);
                            setShowSpeakingSlotModal(true);
                          }
                        }}
                        className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                          selectedSlot === slot.id ? 'bg-blue-50 border-blue-300' : ''
                        }`}
                      >
                        {manualBookingType === 'partial' ? (
                          <div>
                            <div className="font-semibold">{slot.module}</div>
                            <div className="text-sm text-gray-600">
                              {formatDate(slot.exam_date)} • {formatTimeRange(slot.start_time, slot.end_time)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {slot.room && `Room: ${slot.room} • `}
                              {slot.campus && `${slot.campus} • `}
                              Available: {slot.remaining_seats}/{slot.total_slots}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-semibold">Mock Test - {slot.mock_uid}</div>
                            <div className="text-sm text-gray-600">
                              LRW: {formatDate(slot.lrw_date)} • {formatTimeRange(slot.lrw_start, slot.lrw_end)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {slot.campus && `${slot.campus} • `}
                              Available: {slot.lrw_remaining_seats}/{slot.lrw_total_seats}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowManualBooking(false);
                setStudentType('batch');
                setSelectedStudent('');
                setOtherStudentData({ name: '', batch_no: '', candidate_no: '', phone: '', email: '', previous_mock_at_radiance: null, radiance_mock_number: '' });
                setSelectedSlot('');
                setAvailableSlots([]);
                setManualBookingFilters({ module: 'LISTENING', exam_date: '', category: '' });
              }}
              disabled={bookingInProgress}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleManualBooking}
              disabled={
                (studentType === 'batch' && !selectedStudent) ||
                (studentType === 'other' && (
                  !otherStudentData.name || 
                  !otherStudentData.batch_no || 
                  !otherStudentData.candidate_no ||
                  !manualBookingFilters.category
                )) ||
                !selectedSlot || 
                bookingInProgress
              }
              className="bg-gradient-to-r from-purple-600 to-pink-600"
            >
              {bookingInProgress ? 'Booking...' : 'Book Slot'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Speaking Slot Selection Modal (For Mock Manual Booking) */}
      <Dialog open={showSpeakingSlotModal} onOpenChange={setShowSpeakingSlotModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Speaking Slot</DialogTitle>
            <DialogDescription>
              Mock Test: {selectedMockForSpeaking?.mock_uid}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {loadingSpeakingSlots ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                <p className="text-sm text-gray-600 mt-3">Loading speaking slots...</p>
              </div>
            ) : availableSpeakingSlots.length === 0 ? (
              <div className="text-center py-8 text-gray-600">
                <p>No available speaking slots for this mock test.</p>
                <p className="text-sm mt-2">You can book LRW only and add speaking later.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Select Speaking Slot (Available: {availableSpeakingSlots.filter(s => !s.is_booked).length}/{availableSpeakingSlots.length})</Label>
                <div className="border rounded-lg max-h-96 overflow-y-auto">
                  {availableSpeakingSlots.map((slot) => {
                    const isBooked = slot.is_booked === true;
                    return (
                      <div
                        key={slot.id}
                        onClick={() => {
                          if (!isBooked) {
                            setSelectedSpeakingSlot(slot.id);
                          }
                        }}
                        className={`p-4 border-b transition-colors ${
                          isBooked 
                            ? 'bg-gray-100 cursor-not-allowed opacity-60' 
                            : 'cursor-pointer hover:bg-gray-50'
                        } ${
                          selectedSpeakingSlot === slot.id ? 'bg-blue-50 border-blue-300 border-2' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-semibold text-gray-900">
                                {formatDate(slot.speak_date)}
                              </div>
                              {isBooked && (
                                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                  Booked
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              Time: {formatTimeRange(slot.start_time, slot.end_time)}
                            </div>
                            {slot.room && (
                              <div className="text-xs text-gray-500 mt-1">
                                Room: {slot.room}
                              </div>
                            )}
                            {isBooked && slot.booked_by && (
                              <div className="text-xs text-red-600 mt-1">
                                Booked by: {slot.booked_by.name}
                              </div>
                            )}
                          </div>
                          {selectedSpeakingSlot === slot.id && !isBooked && (
                            <div className="text-blue-600">
                              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Speaking Mode Selection */}
            {selectedSpeakingSlot && (
              <div className="space-y-4 pt-4 border-t">
                <Label>Speaking Mode</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setManualSpeakingMode('OFFLINE')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      manualSpeakingMode === 'OFFLINE' 
                        ? 'border-blue-600 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-3xl mb-2">🏢</div>
                      <div className="font-semibold">Offline</div>
                      <div className="text-xs text-gray-600 mt-1">At Campus</div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setManualSpeakingMode('ONLINE')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      manualSpeakingMode === 'ONLINE' 
                        ? 'border-green-600 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-3xl mb-2">📱</div>
                      <div className="font-semibold">Online</div>
                      <div className="text-xs text-gray-600 mt-1">Via WhatsApp</div>
                    </div>
                  </button>
                </div>
                
                {manualSpeakingMode === 'ONLINE' && (
                  <div className="space-y-3 animate-fadeIn">
                    <div>
                      <label className="text-sm font-medium mb-1 block">WhatsApp Number * (11 digits)</label>
                      <input
                        type="tel"
                        value={manualWhatsappNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          setManualWhatsappNumber(value);
                        }}
                        placeholder="01712345678"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        maxLength="11"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {manualWhatsappNumber.length}/11 digits
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between items-center pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  // Book LRW only (skip speaking)
                  setShowSpeakingSlotModal(false);
                  handleManualBooking();
                }}
                disabled={bookingInProgress}
              >
                Book LRW Only (Skip Speaking)
              </Button>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSpeakingSlotModal(false);
                    setSelectedSpeakingSlot('');
                    setSelectedSlot('');
                    setSelectedMockForSpeaking(null);
                  }}
                  disabled={bookingInProgress}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBookMockWithSpeaking}
                  disabled={!selectedSpeakingSlot || bookingInProgress}
                  className="bg-gradient-to-r from-purple-600 to-pink-600"
                >
                  {bookingInProgress ? 'Booking...' : 'Book Mock with Speaking'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Student Profile Modal */}
      {showStudentProfile && selectedStudentForProfile && (
        <StudentProfile
          student={selectedStudentForProfile}
          userRole={user?.role}
          onClose={() => {
            setShowStudentProfile(false);
            setSelectedStudentForProfile(null);
          }}
          onUpdate={() => {
            fetchStudents();
            setShowStudentProfile(false);
            setSelectedStudentForProfile(null);
          }}
        />
      )}

      {/* Speaking Slots Management Modal */}
      <Dialog open={showSpeakingSlotsModal} onOpenChange={setShowSpeakingSlotsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Speaking Slots - {selectedMockForSlots?.mock_uid}</DialogTitle>
            <DialogDescription>
              Manage speaking slots for this mock exam
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {loadingSlots ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : speakingSlots.length === 0 ? (
              <div className="text-center py-8 text-gray-600">
                No speaking slots found for this mock exam
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {speakingSlots.map((slot) => (
                  <div 
                    key={slot.id}
                    className={`p-3 rounded-lg border-2 ${
                      slot.is_booked 
                        ? 'bg-red-50 border-red-300' 
                        : 'bg-green-50 border-green-300'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-semibold">{formatDate(slot.speak_date)}</div>
                        <div className="text-sm text-gray-700">
                          {formatTimeRange(slot.start_time, slot.end_time)}
                        </div>
                        {slot.room && (
                          <div className="text-xs text-gray-600 mt-1">Room: {slot.room}</div>
                        )}
                        {slot.is_booked && slot.booked_by && (
                          <div className="mt-2 text-xs space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="destructive" className="text-xs">Booked</Badge>
                              {slot.speak_mode && (
                                <Badge className={slot.speak_mode === 'ONLINE' ? 'bg-green-600 text-xs' : 'bg-blue-600 text-xs'}>
                                  {slot.speak_mode === 'ONLINE' ? '📱 Online' : '🏢 Offline'}
                                </Badge>
                              )}
                            </div>
                            <div className="text-gray-600">
                              {slot.booked_by.name}
                            </div>
                            {slot.speak_mode === 'ONLINE' && slot.whatsapp_number && (
                              <div className="text-green-700 font-semibold">
                                WhatsApp: {slot.whatsapp_number}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {!slot.is_booked && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-100"
                          onClick={() => handleDeleteSpeakingSlot(slot.id)}
                        >
                          🗑️
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={() => {
                setShowSpeakingSlotsModal(false);
                setSelectedMockForSlots(null);
                setSpeakingSlots([]);
              }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partial Export Modal */}
      <Dialog open={showPartialExportModal} onOpenChange={setShowPartialExportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Partial Bookings</DialogTitle>
            <DialogDescription>
              Select filters to export specific partial exam data
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Category Filter */}
            <div>
              <Label>Category *</Label>
              <Select 
                value={exportFilters.category} 
                onValueChange={(value) => {
                  setExportFilters({...exportFilters, category: value, module: '', exam_id: ''});
                  setAvailableExams([]);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PB">PB</SelectItem>
                  <SelectItem value="CD">CD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Module Filter */}
            {exportFilters.category && (
              <div>
                <Label>Module *</Label>
                <Select 
                  value={exportFilters.module} 
                  onValueChange={(value) => {
                    setExportFilters({...exportFilters, module: value, exam_id: ''});
                    fetchExamsForExport(value, exportFilters.category);
                    setAvailableExams([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Module" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LISTENING">Listening</SelectItem>
                    <SelectItem value="READING">Reading</SelectItem>
                    <SelectItem value="WRITING">Writing</SelectItem>
                    <SelectItem value="SPEAKING">Speaking (Shared)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {exportFilters.module && exportFilters.module === 'SPEAKING' ? (
              <div>
                <Label>Select Date (Optional)</Label>
                <Select 
                  value={exportFilters.exam_id} 
                  onValueChange={(value) => setExportFilters({...exportFilters, exam_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Dates" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Dates</SelectItem>
                    {/* Group exams by date for Speaking */}
                    {[...new Set(availableExams.map(exam => exam.exam_date))].sort().map((date) => (
                      <SelectItem key={date} value={date}>
                        {format(new Date(date), 'EEEE, dd MMM yyyy')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : exportFilters.module && (
              <div>
                <Label>Specific Exam (Optional)</Label>
                <Select 
                  value={exportFilters.exam_id} 
                  onValueChange={(value) => setExportFilters({...exportFilters, exam_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Exams" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Exams</SelectItem>
                    {availableExams.map((exam) => (
                      <SelectItem key={exam.id} value={exam.id}>
                        {format(new Date(exam.exam_date), 'dd MMM yyyy')} - {exam.start_time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowPartialExportModal(false)}
              disabled={exportingCSV}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExportPartialCSV}
              disabled={!exportFilters.category || !exportFilters.module || exportingCSV}
              className="bg-blue-600"
            >
              {exportingCSV ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mock Export Modal */}
      <Dialog open={showMockExportModal} onOpenChange={setShowMockExportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Mock Bookings</DialogTitle>
            <DialogDescription>
              Select filters to export specific mock exam data
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Category *</Label>
              <Select 
                value={exportFilters.category} 
                onValueChange={(value) => {
                  setExportFilters({...exportFilters, category: value, mock_id: ''});
                  fetchMocksForExport(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PB">Paper-Based (PB)</SelectItem>
                  <SelectItem value="CD">Computer-Delivered (CD)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {exportFilters.category && (
              <div>
                <Label>Mock Exam (Optional)</Label>
                <Select 
                  value={exportFilters.mock_id} 
                  onValueChange={(value) => setExportFilters({...exportFilters, mock_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Mock Exams" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Mock Exams</SelectItem>
                    {availableMocks
                      .filter(mock => mock.category === exportFilters.category)
                      .map((mock) => (
                        <SelectItem key={mock.id} value={mock.id}>
                          {mock.mock_uid} - {format(new Date(mock.lrw_date), 'dd MMM yyyy')}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowMockExportModal(false)}
              disabled={exportingCSV}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExportMockCSV}
              disabled={!exportFilters.category || exportingCSV}
              className="bg-blue-600"
            >
              {exportingCSV ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Password Change Dialog */}
      <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-6 h-6 text-blue-600" />
              Change Admin Password
            </DialogTitle>
            <DialogDescription>
              Change your admin account password
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="admin_current_password">Current Password</Label>
              <Input
                id="admin_current_password"
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                placeholder="Enter current password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin_new_password">New Password</Label>
              <Input
                id="admin_new_password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin_confirm_password">Confirm New Password</Label>
              <Input
                id="admin_confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                placeholder="Confirm new password"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Password Requirements:</strong>
              <ul className="list-disc ml-5 mt-1 space-y-1">
                <li>Minimum 6 characters</li>
                <li>Make sure both new passwords match</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button 
              variant="outline"
              onClick={() => {
                setShowPasswordChange(false);
                setPasswordData({
                  current_password: '',
                  new_password: '',
                  confirm_password: ''
                });
              }}
              disabled={changingPassword}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordChange}
              disabled={changingPassword}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
